import React, { useEffect, useState } from "react";
import { Offcanvas, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { FiColumns, FiSearch, FiX } from "react-icons/fi";
import {
  COLUMN_GROUPS,
  COLUMN_ORDER,
  COLUMN_PRESETS,
  NEW_COLUMNS,
  columnLabelKey,
  getDefaultColumns,
  matchingPreset,
} from "./reportColumns";
import { IconInput, Toggle } from "./ReportControls";

const ColumnPicker = ({ isOpen, onClose, selected, remember, onApply }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(selected);
  const [rememberDraft, setRememberDraft] = useState(remember);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (isOpen) {
      setDraft(selected);
      setRememberDraft(remember);
      setQuery("");
    }
  }, [isOpen, selected, remember]);

  // Keep the table column order whatever order the boxes are ticked in.
  const ordered = (list) => COLUMN_ORDER.filter((c) => list.includes(c));
  const toggleColumn = (column) =>
    setDraft((d) => ordered(d.includes(column) ? d.filter((c) => c !== column) : [...d, column]));
  const toggleGroup = (columns, on) =>
    setDraft((d) => ordered(on ? [...new Set([...d, ...columns])] : d.filter((c) => !columns.includes(c))));

  const q = query.trim().toLowerCase();
  const matches = (column) => !q || t(columnLabelKey(column)).toLowerCase().includes(q) || column.toLowerCase().includes(q);
  const activePreset = matchingPreset(draft);
  const isRtl = document.body.dir === "rtl";

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction={isRtl ? "start" : "end"} className="mr-drawer">
      <div className="mr-drawer-head">
        <span className="mr-icon-tile">
          <FiColumns size={20} />
        </span>
        <div>
          <h3>{t("member_report_choose_columns")}</h3>
          <p>{t("member_report_choose_columns_hint")}</p>
        </div>
        <button type="button" className="mr-close" onClick={onClose} aria-label={t("common_cancel")}>
          <FiX size={16} />
        </button>
      </div>

      <div className="mr-drawer-tools">
        <IconInput icon={<FiSearch size={15} />}>
          <Input value={query} placeholder={t("member_report_search_columns")} onChange={(e) => setQuery(e.target.value)} />
        </IconInput>
        <div className="mr-presets">
          {!activePreset && <span className="mr-preset mr-on">{t("member_report_preset_custom")}</span>}
          {COLUMN_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={"mr-preset" + (activePreset === p.key ? " mr-on" : "")}
              onClick={() => setDraft(ordered(p.columns))}
            >
              {t(p.label)}
            </button>
          ))}
        </div>
      </div>

      <div className="mr-drawer-body">
        {COLUMN_GROUPS.map((group) => {
          const visible = group.columns.filter(matches);
          if (visible.length === 0) return null;
          const onCount = group.columns.filter((c) => draft.includes(c)).length;
          const all = onCount === group.columns.length;
          return (
            <div key={group.key} className="mr-cgroup">
              <div className="mr-cgroup-head">
                <label>
                  <input
                    type="checkbox"
                    checked={all}
                    ref={(el) => el && (el.indeterminate = onCount > 0 && !all)}
                    onChange={() => toggleGroup(group.columns, !all)}
                  />
                  {t(group.label)}
                </label>
                <small>{t("member_report_n_of_total", { on: onCount, total: group.columns.length })}</small>
              </div>
              <div className="mr-copts">
                {visible.map((column) => {
                  const on = draft.includes(column);
                  return (
                    <label key={column} className={"mr-copt" + (on ? " mr-on" : "")}>
                      <input type="checkbox" checked={on} onChange={() => toggleColumn(column)} />
                      {t(columnLabelKey(column))}
                      {NEW_COLUMNS.includes(column) && <span className="mr-new">{t("member_report_new")}</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mr-drawer-foot">
        <div className="mr-summary">
          <span>{t("member_report_columns_selected", { on: draft.length, total: COLUMN_ORDER.length })}</span>
          <Toggle on={rememberDraft} onChange={setRememberDraft} title={t("member_report_remember_columns")} />
        </div>
        <button type="button" className="mr-btn mr-btn-ghost mr-sm" onClick={() => setDraft(getDefaultColumns())}>
          {t("member_report_reset_default")}
        </button>
        <button
          type="button"
          className="mr-btn mr-btn-primary mr-sm"
          disabled={draft.length === 0}
          onClick={() => onApply(draft, rememberDraft)}
        >
          {t("member_report_apply")}
        </button>
      </div>
    </Offcanvas>
  );
};

export default ColumnPicker;
